import { i18n } from '@/main';
import { helpers, minLength, sameAs, requiredIf } from 'vuelidate/lib/validators';
import { required, maxLength, email } from 'vuelidate/lib/validators';

const iban = helpers.regex(
  'iban',
  /^(?:(?:IT|SM)\d{2}[A-Z]\d{22}|CY\d{2}[A-Z]\d{23}|NL\d{2}[A-Z]{4}\d{10}|LV\d{2}[A-Z]{4}\d{13}|(?:BG|BH|GB|IE)\d{2}[A-Z]{4}\d{14}|GI\d{2}[A-Z]{4}\d{15}|RO\d{2}[A-Z]{4}\d{16}|KW\d{2}[A-Z]{4}\d{22}|MT\d{2}[A-Z]{4}\d{23}|NO\d{13}|(?:DK|FI|GL|FO)\d{16}|MK\d{17}|(?:AT|EE|KZ|LU|XK)\d{18}|(?:BA|HR|LI|CH|CR)\d{19}|(?:GE|DE|LT|ME|RS)\d{20}|IL\d{21}|(?:AD|CZ|ES|MD|SA)\d{22}|PT\d{23}|(?:BE|IS)\d{24}|(?:FR|MR|MC)\d{25}|(?:AL|DO|LB|PL)\d{26}|(?:AZ|HU)\d{27}|(?:GR|MU)\d{28})$/
);

const phone = helpers.regex('phone', /^\+(?:[0-9] ?){6,14}[0-9]$/);

export default class InputValidationsService {
  private address = {
    street: {
      required: requiredIf(v => v.isDeliver),
      maxLength: maxLength(30),
    },
    number: {
      required: requiredIf(v => v.isDeliver),
      maxLength: maxLength(30),
    },
    addressAddition: {
      maxLength: maxLength(30),
    },
    zipcode: {
      required: requiredIf(v => v.isDeliver),
      maxLength: maxLength(30),
    },
    city: {
      required: requiredIf(v => v.isDeliver),
      maxLength: maxLength(30),
    },
    phone: {
      required: requiredIf(v => v.isDeliver),
      maxLength: maxLength(30),
    },
  };

  private bank = {
    bankAccountName: {
      required,
      maxLength: maxLength(30),
    },
    ibanNumber: {
      required,
      iban,
    },
  };

  private user = {
    email: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(254),
      email,
    },
    firstName: { required, minLength: minLength(1), maxLength: maxLength(50) },
    lastName: { required, minLength: minLength(1), maxLength: maxLength(50) },
  };

  private marketer = {
    address: this.address,
    ...this.bank,
  };

  private maxSize(value: File[], size: number) {
    if (!value) {
      return true;
    }
    let sum = 0;
    value.forEach(file => {
      sum += file.size;
    });
    if (sum > size) {
      return false;
    }
    return true;
  }

  public restaurantDocs = {
    menuCardFiles: {
      required,
      maxSize10000000: value => {
        return this.maxSize(value, 10000000);
      },
    },
    idCardFiles: {
      required,
      maxSize10000000: value => {
        return this.maxSize(value, 10000000);
      },
    },
    cocRegistrationFiles: {
      required,
      maxSize10000000: value => {
        return this.maxSize(value, 10000000);
      },
    },
    bankCardFiles: {
      required,
      maxSize10000000: value => {
        return this.maxSize(value, 10000000);
      },
    },
  };

  public signupForm = {
    confirmPassword: {
      sameAsPassword: sameAs(function () {
        return this.user.password;
      }),
    },
    user: {
      password: {
        required,
        minLength: minLength(8),
        valid: function (value) {
          const containsUppercase = /[A-Z]/.test(value);
          const containsLowercase = /[a-z]/.test(value);
          const containsNumber = /[0-9]/.test(value);
          return containsUppercase && containsLowercase && containsNumber;
        },
      },
      ...this.user,
    },
  };

  public loginForm = {
    email: {
      required,
      minLength: minLength(5),
      maxLength: maxLength(254),
      email,
    },
    password: {
      required,
    },
  };

  public marketerProfile = {
    marketer: {
      user: this.user,
      ...this.marketer,
    },
  };

  public marketerForm = {
    checkbox: {
      checked(val) {
        return val;
      },
    },
    policy: {
      checked(val) {
        return val;
      },
    },
    marketer: {
      ...this.marketer,
    },
  };

  public restaurantForm = {
    step1: {
      name: {
        required,
        maxLength: maxLength(64),
      },
      collaborationMethods: {
        required,
        maxLength: maxLength(30),
      },
      taxIdentificationNumber: {
        required,
        maxLength: maxLength(32),
      },
      checkbox: {
        checked(val) {
          return val;
        },
      },
      policy: {
        checked(val) {
          return val;
        },
      },
      ...this.address,
      phone: {
        required,
        phone,
      },
    },
    step3: {
      ...this.bank,
      dateOfBirth: {
        required,
      },
    },
    step4: {
      restaurantCategories: {
        required,
      },
      logoFile: {
        required,
        maxSize5000000: value => !value || value.size < 5000000,
      },
      imageFile: {
        required,
        maxSize5000000: value => !value || value.size < 5000000,
      },
    },
  };

  public menusManagement = {
    menuCategories: {
      menuCategory: {
        title: {
          text: {
            required,
            maxLength: maxLength(30),
          },
        },
        menu: {
          title: {
            text: {
              required,
              maxLength: maxLength(30),
            },
          },
        },
      },

      newMenuCategoryTitle: {
        text: {
          maxLength: maxLength(30),
        },
      },
      menuVariant: {
        price: {
          newPrice: {
            required,
          },
        },
        title: {
          text: {
            required,
          },
        },
      },
      addonCategory: {
        title: {
          text: {
            required,
          },
        },
        addons: {
          $each: {
            title: {
              text: {
                required,
              },
            },
            price: {
              newPrice: {
                required,
              },
            },
          },
        },
      },
    },
  };

  public checkout = {
    addresses: { ...this.address },
    user: { ...this.user },
  };

  public restaurantDiscounts = {
    foodDiscount: {
      amount: {
        required,
        min: value => !value || value >= 0,
      },
    },
    nonfoodDiscount: {
      amount: {
        required,
        min: value => !value || value >= 0,
      },
    },
  };

  public orderDialog = {
    preparingTime: {
      required,
    },
  };

  public loginErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.required && errors.push(i18n.t('register.messages.validate.login.required'));
    !entity.maxLength && errors.push(i18n.t('register.messages.validate.login.maxlength'));
    !entity.minLength && errors.push(i18n.t('register.messages.validate.login.minlength'));
    !entity.pattern && errors.push(i18n.t('register.messages.validate.login.pattern'));
    return errors;
  }
  public emailErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.required && errors.push(i18n.t('global.messages.validate.email.required'));
    !entity.maxLength && errors.push(i18n.t('global.messages.validate.email.maxlength'));
    !entity.minLength && errors.push(i18n.t('global.messages.validate.email.minlength'));
    !entity.email && errors.push(i18n.t('global.messages.validate.email.invalid'));
    return errors;
  }
  public firstNameErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.required && errors.push(i18n.t('register.messages.validate.firstName.required'));
    !entity.maxLength && errors.push(i18n.t('register.messages.validate.firstName.maxlength'));
    !entity.minLength && errors.push(i18n.t('register.messages.validate.firstName.minlength'));
    return errors;
  }
  public lastNameErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.required && errors.push(i18n.t('register.messages.validate.lastName.required'));
    !entity.maxLength && errors.push(i18n.t('register.messages.validate.lastName.maxlength'));
    !entity.minLength && errors.push(i18n.t('register.messages.validate.lastName.minlength'));
    return errors;
  }
  public passwordErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.required && errors.push(i18n.t('register.messages.validate.password.required'));
    !entity.minLength && errors.push(i18n.t('register.messages.validate.password.validation'));
    !entity.valid && errors.push(i18n.t('register.messages.validate.password.validation'));
    return errors;
  }
  public passwordErrorsForLogin(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.required && errors.push(i18n.t('register.messages.validate.password.required'));
    return errors;
  }
  public confirmPasswordErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.sameAsPassword && errors.push(i18n.t('register.messages.validate.password.compare'));
    return errors;
  }

  public errors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    if (entity.required !== undefined && !entity.required) {
      errors.push(i18n.t('entity.validation.required'));
    }
    if (!entity.maxLength) {
      errors.push(i18n.t('entity.validation.maxlength', { max: 30 }));
    }
    return errors;
  }

  public max30Errors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    if (!entity.required) {
      errors.push(i18n.t('entity.validation.required'));
    }
    if (!entity.maxLength) {
      errors.push(i18n.t('entity.validation.maxlength', { max: 30 }));
    }
    return errors;
  }

  public justMax30Errors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    if (!entity.maxLength) {
      errors.push(i18n.t('entity.validation.maxlength', { max: 30 }));
    }
    return errors;
  }

  public ibanError(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    if (entity.required !== undefined && !entity.required) {
      errors.push(i18n.t('entity.validation.required'));
    }
    if (!entity.iban) {
      errors.push(i18n.t('entity.validation.iban'));
    }
    return errors;
  }

  public phoneError(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    if (entity.required !== undefined && !entity.required) {
      errors.push(i18n.t('entity.validation.required'));
    }
    if (!entity.phone) {
      errors.push(i18n.t('entity.validation.phone'));
    }
    return errors;
  }

  checkboxErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.checked && errors.push(i18n.t('entity.validation.required'));
    return errors;
  }

  required(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    !entity.required && errors.push(i18n.t('entity.validation.required'));
    return errors;
  }

  fileSize(entity, size, printSize, required) {
    const errors = [];
    if (!entity.$dirty) return errors;
    if (required) {
      !entity.required && errors.push(i18n.t('entity.validation.required'));
    }
    !entity[`maxSize${size}`] && errors.push(i18n.t('signupRestaurant.step4.hint', { size: printSize }));
    return errors;
  }

  public discountErrors(entity) {
    const errors = [];
    if (!entity.$dirty) return errors;
    if (entity.required !== undefined && !entity.required) {
      errors.push(i18n.t('entity.validation.required'));
    }
    if (!entity.min) {
      errors.push(i18n.t('entity.validation.min', { min: 5 }));
    }
    return errors;
  }
}
